import type { EpicWithCalculations } from './review-data';
import { authenticatedFetch } from '../api/shared/authenticatedFetch';

export type { EpicWithCalculations };

export const ADO_WORK_ITEM_BASE_URL = 'https://dev.azure.com/access-devops/_workitems/edit';

export interface EpicDetails {
  id: number;
  title: string;
  state: string;
  active_date: string | null;
  closed_date: string | null;
  capex_percentage: number | null;
  r_and_d_percentage: number | null;
  story_points: number | null;
  effort: number | null;
  original_estimate: number | null;
  project_name: string;
  description: string | null;
  url: string | null;
  data_source: string | null;
}

export interface ClaudeAssessmentDetails {
  id: number;
  epic_id: string;
  capex_percentage: number;
  r_and_d_percentage: number;
  capex_rationale: string;
  r_and_d_rationale: string;
  capex_eligible_work: string | null;
  capex_non_eligible_work: string | null;
  r_and_d_eligible_work: string | null;
  r_and_d_non_eligible_work: string | null;
  assessed_at: string;
}

export interface EpicOverridePayload {
  manualCapexPercentage: number;
  manualRndPercentage: number;
  selectedPercentage: string;
  capexSource?: string;
  rndSource?: string;
  epicSize: string;
  manualEstimateValue?: number | null;
  status: string;
}

/**
 * Fetch detailed information about an EPIC
 */
export async function getEpicDetails(apiUrl: string, epicId: string): Promise<EpicDetails | null> {
  try {
    const response = await authenticatedFetch(`${apiUrl}/db/epic-details/${epicId}`);
    const data = await response.json();
    return data.epic;
  } catch (error) {
    console.error('ProjectApproval_ Error fetching epic details:', error);
    throw error;
  }
}

/**
 * Fetch detailed Claude assessment for an EPIC
 */
export async function getClaudeAssessmentDetails(apiUrl: string, epicId: string): Promise<ClaudeAssessmentDetails | null> {
  try {
    const response = await authenticatedFetch(`${apiUrl}/db/claude-assessment-details/${epicId}`);
    const data = await response.json();
    return data.assessment;
  } catch (error) {
    console.error('ProjectApproval_ Error fetching Claude assessment details:', error);
    throw error;
  }
}

/**
 * Save or update an epic override using PUT /epics/{epicId}/overrides
 */
export async function saveEpicOverride(
  apiUrl: string,
  epicId: string,
  data: EpicOverridePayload
): Promise<void> {
  try {
    await authenticatedFetch(`${apiUrl}/epics/${epicId}/overrides`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  } catch (error) {
    console.error('ProjectApproval_ Error saving epic override:', error);
    throw error;
  }
}

export interface ChildWorkItem {
  work_item_id: string;
  url: string | null;
  title?: string;
  state?: string;
}

/**
 * Fetch child work items for a FLAT synthetic parent epic via the assessment summary endpoint
 */
export async function getEpicChildWorkItems(
  apiUrl: string,
  epicId: string,
  projectName: string,
  fiscalYearQuarter: string,
): Promise<ChildWorkItem[]> {
  try {
    const queryString = `projectName=${encodeURIComponent(projectName)}&fiscalYearQuarter=${encodeURIComponent(fiscalYearQuarter)}`;
    const response = await authenticatedFetch(`${apiUrl}/epic-assessments/summary?${queryString}`);
    const data: Array<{ epic_id: string; children?: ChildWorkItem[] }> = await response.json();
    const match = data.find((s) => s.epic_id === epicId);
    return match?.children ?? [];
  } catch (error) {
    console.error('ProjectApproval_ Error fetching epic child work items:', error);
    return [];
  }
}

export interface BulkEpicOverrideItem extends EpicOverridePayload {
  epicId: string;
}

/**
 * Bulk save or update epic overrides in a single API call using PUT /epics/overrides/bulk
 */
export async function saveBulkEpicOverrides(
  apiUrl: string,
  overrides: BulkEpicOverrideItem[]
): Promise<void> {
  try {
    await authenticatedFetch(`${apiUrl}/epics/overrides/bulk`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ overrides }),
    });
  } catch (error) {
    console.error('ProjectApproval_ Error saving bulk epic overrides:', error);
    throw error;
  }
}
